import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Col,
  Row,
  Statistic,
  Table,
  Spin,
  message,
  Tag,
  Typography,
  Space,
  Select,
  Empty,
} from 'antd';
import { Line, Column, Pie, Heatmap } from '@ant-design/plots';
import { AlertTriangle, Timer, Server, Users, Hourglass, Percent } from 'lucide-react';
import { useAdminSocketContext } from '../../sockets/AdminSocketContext';
import AdminDateFilters, { resolvePresetRange, filtersToParams } from './AdminDateFilters';
import AdminKpiCard from './AdminKpiCard';
import { AdminOnlinePopover } from './AdminOnlineIndicator';
import { getRequestGroupKey, getRequestGroupLabel, aggregateCountsByGroup } from '../../utils/requestTypeLabels';
import { auditEventMeta, formatShortDateTime, statusMeta } from './adminFormatters';
import { useTheme } from '../../theme/ThemeProvider';
import './adminLayout.sass';

const { Text } = Typography;

const makeDefaultFilters = () => ({
  preset: '30',
  dateRange: resolvePresetRange('30'),
  userId: null,
  period: 'day',
});

const DEFAULT_FILTERS = makeDefaultFilters();

const formatDuration = (sec) => {
  if (sec == null || sec === 0) return '—';
  if (sec < 60) return `${sec} сек`;
  if (sec < 3600) return `${Math.round(sec / 60)} мин`;
  return `${(sec / 3600).toFixed(1)} ч`;
};

const formatTs = (ts) => (ts ? new Date(ts).toLocaleString('ru-RU') : '—');

const DOW_LABELS = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

function groupRequestTypeRows(rows, typeField = 'request_type') {
  const map = aggregateCountsByGroup(
    (rows || []).map((r) => ({
      type: r[typeField] || r.type,
      count: r.count,
    })),
    'type',
    'count'
  );
  return Object.entries(map).map(([key, count]) => ({
    type: getRequestGroupLabel(key),
    count,
  }));
}

const AdminTechMonitor = () => {
  const { darkMode } = useTheme();
  const api = useAdminSocketContext();
  const { onlineUserIds } = api;
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [errorsSeries, setErrorsSeries] = useState([]);
  const [errorsRecent, setErrorsRecent] = useState([]);
  const [errorsByType, setErrorsByType] = useState([]);
  const [byType, setByType] = useState([]);
  const [byStatus, setByStatus] = useState([]);
  const [durations, setDurations] = useState([]);
  const [peakHours, setPeakHours] = useState([]);
  const [peakDow, setPeakDow] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [downtime, setDowntime] = useState(null);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [activity, setActivity] = useState(null);
  const [pendingAged, setPendingAged] = useState(0);
  const [peakMetric, setPeakMetric] = useState('requests');
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  useEffect(() => {
    api.getFiltersMeta().then((res) => setMeta(res.meta)).catch(() => {});
  }, [api]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = filtersToParams(filters);
    try {
      const [
        ov,
        errSeries,
        errRecent,
        errTypes,
        types,
        statuses,
        dur,
        peaks,
        dow,
        heat,
        down,
        pipeline,
        recent,
        pending,
      ] = await Promise.all([
        api.getOverview(params),
        api.getTimeSeries({ ...params, metric: 'requests', status: 'error' }),
        api.getErrorsRecent({ ...params, limit: 30 }),
        api.getRequestsByType({ ...params, status: 'error' }),
        api.getRequestsByType(params),
        api.getRequestsByStatus(params),
        api.getRequestDurations(params),
        api.getPeakLoad({ ...params, dimension: 'hour', metric: peakMetric }),
        api.getPeakLoad({ ...params, dimension: 'dow', metric: peakMetric }),
        api.getPeakLoad({ ...params, dimension: 'heatmap', metric: peakMetric }),
        api.getDowntime(params),
        api.getPipelineStages(params),
        api.getRecentActivity({ limit: 15 }),
        api.getPendingAged(params),
      ]);

      setOverview(ov.overview);
      setErrorsSeries(
        (errSeries.series || []).map((r) => ({
          date: new Date(r.ts).toLocaleDateString('ru-RU'),
          count: r.count,
        }))
      );
      setErrorsRecent(errRecent.rows || []);
      setErrorsByType(groupRequestTypeRows(errTypes.rows));
      setByType(groupRequestTypeRows(types.rows));
      const statusCounts = (statuses.rows || []).reduce((acc, row) => {
        const label = statusMeta(row.status).label;
        acc[label] = (acc[label] || 0) + Number(row.count || 0);
        return acc;
      }, {});
      setByStatus(
        Object.entries(statusCounts).map(([status, count]) => ({ status, count }))
      );
      setDurations(dur.rows || []);
      setPeakHours(
        (peaks.rows || []).map((r) => ({ hour: r.label, count: r.count }))
      );
      setPeakDow(
        (dow.rows || []).map((r) => ({
          day: DOW_LABELS[r.key] || r.label,
          count: r.count,
        }))
      );
      setHeatmapData(
        (heat.rows || []).map((r) => ({
          day: DOW_LABELS[r.dow] || String(r.dow),
          hour: `${String(r.hour).padStart(2, '0')}:00`,
          count: r.count,
        }))
      );
      setDowntime(down.downtime);
      setPipelineStages((pipeline.rows || []).map((r) => ({ stage: r.stage, count: r.count })));
      setActivity(recent.activity);
      setPendingAged(pending.count || 0);
      setLastLoadedAt(new Date().toISOString());
    } catch (e) {
      message.error(e.message || 'Не удалось загрузить статистику');
    } finally {
      setLoading(false);
    }
  }, [api, filters, peakMetric]);

  useEffect(() => {
    load();
  }, [load]);

  const errorRate =
    overview?.requests_period > 0
      ? ((overview.error_period / overview.requests_period) * 100).toFixed(1)
      : '0';

  return (
    <div>
      <AdminDateFilters
        filters={filters}
        onChange={setFilters}
        onApply={load}
        onReset={() => setFilters(makeDefaultFilters())}
        loading={loading}
        meta={meta}
        showUser={false}
        showPeriodGranularity={false}
      />

      <Space size={12} wrap className="admin-list-meta">
        {lastLoadedAt && (
          <span className="admin-list-meta__muted">
            Обновлено: {formatShortDateTime(lastLoadedAt)}
          </span>
        )}
      </Space>

      <Spin spinning={loading}>
        <p className="admin-section-label">Здоровье системы</p>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={8} lg={4}>
            <AdminKpiCard
              title="Ошибок за период"
              value={overview?.error_period ?? 0}
              icon={AlertTriangle}
              variant="red"
              hint="Запросы со статусом ошибки за выбранный период"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <AdminKpiCard
              title="Доля ошибок"
              value={`${errorRate}%`}
              icon={Percent}
              variant="orange"
              hint="Отношение ошибок ко всем запросам за период"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <AdminKpiCard
              title="В очереди > 30 мин"
              value={pendingAged}
              icon={Hourglass}
              variant="orange"
              hint="Запросы в статусе «В очереди» старше 30 минут"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <AdminOnlinePopover onlineUserIds={onlineUserIds} users={meta?.users}>
              <div className="admin-kpi-card--clickable">
                <AdminKpiCard
                  title="Онлайн"
                  value={overview?.online_now ?? onlineUserIds?.length ?? 0}
                  icon={Users}
                  variant="green"
                  hint="Пользователи с активной сессией в сервисе"
                />
              </div>
            </AdminOnlinePopover>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <AdminKpiCard
              title="Простой"
              value={formatDuration(downtime?.total_downtime_sec)}
              icon={Server}
              variant="purple"
              hint="Суммарное время недоступности сервиса за период"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <AdminKpiCard
              title="MTTR"
              value={formatDuration(downtime?.mttr_sec)}
              icon={Timer}
              variant="cyan"
              hint="Среднее время восстановления после сбоя"
            />
          </Col>
        </Row>

        <p className="admin-section-label">Ошибки</p>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Ошибки по времени" className="admin-chart-card">
              {errorsSeries.length > 0 ? (
                <Line data={errorsSeries} xField="date" yField="count" height={260} smooth theme={darkMode ? 'dark' : 'light'} />
              ) : (
                <Text type="secondary">Нет ошибок за период</Text>
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Ошибки по типам запросов" className="admin-chart-card">
              {errorsByType.length > 0 ? (
                <Column data={errorsByType} xField="type" yField="count" height={260} theme={darkMode ? 'dark' : 'light'} />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет данных" />
              )}
            </Card>
          </Col>
        </Row>

        <Card title="Последние ошибки" className="admin-chart-card admin-table-card">
          <Table
            size="small"
            rowKey="id"
            dataSource={errorsRecent}
            pagination={{ pageSize: 10, showTotal: (count) => `Ошибок: ${count}` }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ошибок за период нет" /> }}
            columns={[
              { title: 'Пользователь', dataIndex: 'username', width: 130 },
              {
                title: 'Тип',
                dataIndex: 'request_type',
                width: 190,
                render: (t) => getRequestGroupLabel(getRequestGroupKey(t)),
              },
              {
                title: 'Время',
                dataIndex: 'created_at',
                render: (value) => formatTs(value),
                width: 160,
              },
              { title: 'Детали', dataIndex: 'result_preview', ellipsis: true },
            ]}
          />
        </Card>

        <p className="admin-section-label">Нагрузка и производительность</p>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card title="Запросы по типу" className="admin-chart-card">
              <Column data={byType} xField="type" yField="count" height={280} theme={darkMode ? 'dark' : 'light'} />
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Запросы по статусу" className="admin-chart-card">
              <Pie
                data={byStatus}
                angleField="count"
                colorField="status"
                radius={0.85}
                height={280}
                legend={{ position: 'bottom' }}
                theme={darkMode ? 'dark' : 'light'}
              />
            </Card>
          </Col>
        </Row>

        <Card title="Время выполнения по типам" className="admin-chart-card admin-table-card">
          <Table
            size="small"
            rowKey="request_type"
            dataSource={durations}
            pagination={false}
            columns={[
              { title: 'Тип', dataIndex: 'request_type', render: (t) => getRequestGroupLabel(getRequestGroupKey(t)) },
              { title: 'Кол-во', dataIndex: 'count', width: 80 },
              { title: 'Среднее', dataIndex: 'avg_sec', render: formatDuration, width: 100 },
              { title: 'p50', dataIndex: 'p50_sec', render: formatDuration, width: 90 },
              { title: 'p95', dataIndex: 'p95_sec', render: formatDuration, width: 90 },
              { title: 'Max', dataIndex: 'max_sec', render: formatDuration, width: 90 },
            ]}
          />
        </Card>

        <Card
          title="Пиковые нагрузки"
          className="admin-chart-card"
          extra={
            <Select
              value={peakMetric}
              onChange={setPeakMetric}
              style={{ width: 140 }}
              options={[
                { value: 'requests', label: 'Запросы' },
                { value: 'sessions', label: 'Сессии' },
                { value: 'tasks', label: 'Задачи' },
              ]}
            />
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={8}>
              <Text strong>По часам</Text>
              <Column data={peakHours} xField="hour" yField="count" height={220} theme={darkMode ? 'dark' : 'light'} />
            </Col>
            <Col xs={24} lg={8}>
              <Text strong>По дням недели</Text>
              <Column data={peakDow} xField="day" yField="count" height={220} theme={darkMode ? 'dark' : 'light'} />
            </Col>
            <Col xs={24} lg={8}>
              <Text strong>Heatmap (день × час)</Text>
              {heatmapData.length > 0 ? (
                <Heatmap
                  data={heatmapData}
                  xField="hour"
                  yField="day"
                  colorField="count"
                  height={220}
                  theme={darkMode ? 'dark' : 'light'}
                  legend
                />
              ) : (
                <Text type="secondary">Нет данных</Text>
              )}
            </Col>
          </Row>
        </Card>

        <Card title="Простои сервера" className="admin-chart-card">
          <Space size="large" wrap style={{ marginBottom: 12 }}>
            <Statistic title="Инцидентов (crash)" value={downtime?.crash_count ?? 0} />
            <Statistic title="Суммарный простой" value={formatDuration(downtime?.total_downtime_sec)} />
            <Statistic title="MTTR" value={formatDuration(downtime?.mttr_sec)} />
          </Space>
          <Table
            size="small"
            rowKey={(r, i) => `${r.start}-${i}`}
            dataSource={downtime?.outages || []}
            pagination={false}
            columns={[
              { title: 'Начало', dataIndex: 'start', render: formatTs },
              { title: 'Конец', dataIndex: 'end', render: (v) => (v ? formatTs(v) : '—') },
              { title: 'Длительность', dataIndex: 'duration_sec', render: formatDuration },
              { title: 'Причина', dataIndex: 'cause' },
              { title: 'Сообщение', dataIndex: 'message', ellipsis: true },
            ]}
          />
        </Card>

        {overview?.csv_stats && (
          <Card title="База товарных знаков (CSV)" className="admin-chart-card">
            <Space size="large" wrap>
              <Statistic title="Всего записей" value={overview.csv_stats.total_records} />
              <Statistic title="Активных" value={overview.csv_stats.active_records} />
              <Statistic title="Неактивных" value={overview.csv_stats.inactive_records} />
            </Space>
          </Card>
        )}

        {overview?.online_users?.length > 0 && (
          <Card title="Сейчас онлайн" className="admin-chart-card">
            {overview.online_users.map((u) => (
              <Tag key={u.id} color="green" style={{ marginBottom: 4 }}>
                {u.username} (#{u.id})
              </Tag>
            ))}
          </Card>
        )}

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Pipeline stages" className="admin-chart-card">
              {pipelineStages.length > 0 ? (
                <Column data={pipelineStages} xField="stage" yField="count" height={240} theme={darkMode ? 'dark' : 'light'} />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет данных" />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Последние события аудита" className="admin-chart-card">
              <Table
                size="small"
                rowKey="id"
                pagination={{ pageSize: 6 }}
                dataSource={activity?.audit || []}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Событий нет" /> }}
                columns={[
                  {
                    title: 'Событие',
                    dataIndex: 'event_type',
                    ellipsis: true,
                    render: (value) => {
                      const event = auditEventMeta(value);
                      return <Tag color={event.color}>{event.label}</Tag>;
                    },
                  },
                  {
                    title: 'Кто',
                    render: (_, r) => r.actor_username || r.user_username || '—',
                  },
                  { title: 'Когда', dataIndex: 'created_at', render: formatTs, width: 150 },
                ]}
              />
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default AdminTechMonitor;
